import { useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { Stack, Box, Dialog, DialogContent, Checkbox, CircularProgress } from '@mui/material';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import EditRole from './EditRole';
import autoSizeColumns from '../../reuseable-components/AGGrid/AGGridAutoSizeCols';

EditRoles.propTypes = {
  userId: PropTypes.string,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function EditRoles({ open, close, userId }) {
  const gridRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingRoleList, setLoadingRoleList] = useState(false);
  const [mainRolesSaved, setMainRolesSaved] = useState(false);
  const [formValue, setFormValue] = useState({ roles: [] });
  const [roleData, setRoleData] = useState([]);
  const [editRole, setEditRole] = useState(null);

  useEffect(() => {
    if (open && userId) {
      setLoadingRoleList(true);
      axiosInstance.get(`/user/mainRoles?userId=${userId}`)
        .then(res => {
          setRoleData(res.data ?? []);
          setFormValue({ roles: (res.data ?? []).filter(role => role.active).map(role => role.role) });
        })
        .catch(() => enqueueSnackbar('Get role list failed', { variant: 'error' }))
        .finally(() => setLoadingRoleList(false));
    } else {
      setFormValue({ roles: [] });
      setMainRolesSaved(false);
    }
  }, [open])

  const onSubmitMainRoles = () => {
    setSubmitting(true);
    axiosInstance.post("/user/updateMainRoles", objectToFormData({ ...formValue, userId }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar('Roles updated', { variant: 'success' });
          const rolesWithSubData = roleData.filter(role => formValue.roles.includes(role.role) && role.modelName);
          if (rolesWithSubData.length > 0) setRoleData(rolesWithSubData);
          else close();
          setMainRolesSaved(true);
        }
        else enqueueSnackbar(`Update roles failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`Update roles failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  const updateRowActive = (params, active) => {
    setFormValue(formValue => {
      formValue.roles = active ? [...formValue.roles, params.data.role] : formValue.roles.filter(role => role !== params.data.role);
      return formValue;
    });
    const newNodeData = { ...params.node.data, active };
    params.node.setData(newNodeData);
    params.api.refreshHeader();
  }

  const updateAllRowActives = (params, active) => {
    params.api.forEachNodeAfterFilter(node => {
      node.data.active = !active;
      return node.data.id;
    });
    setFormValue(formValue => {
      formValue.roles = active ? roleData.map(role => role.role) : [];
      return formValue;
    });
    params.api.refreshCells();
    params.api.refreshHeader();
  }

  const columns = useMemo(() => [
    {
      field: 'name',
      minWidth: 150,
      flex: 1
    },
    ...[!mainRolesSaved ? {
      field: 'active',
      cellRenderer: (params) => <Checkbox checked={params.data.active ?? false} onChange={(e) => updateRowActive(params, e.target.checked)} />,
      headerComponent: (params) => AGGridHeaderCheckboxSimple(params, updateAllRowActives),
    } : {
      field: 'actions',
      minWidth: 80,
      cellRenderer: (params) => <Box mr={2}>
        <EditIconButton onClick={() => setEditRole(params.data)} />
      </Box>
    }]
  ], [mainRolesSaved]);

  return <Dialog open={open} onClose={close} maxWidth="xl" >
    <DialogContent>
      <Stack spacing={1} minWidth="1200px">
        <AGGridQuickFilterWrapper gridRef={gridRef} />
        <Box>
          {!loadingRoleList ? <Box sx={{ height: "450px" }}>
            <AgGridReact
              ref={gridRef}
              {...AgGridDefaults}
              columnDefs={columns}
              rowData={roleData}
              onFilterChanged={(params) => params.api.refreshHeader()}
              onNewColumnsLoaded={autoSizeColumns}
            />
          </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
        </Box>
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained"
            onClick={() => mainRolesSaved ? close() : onSubmitMainRoles()}>
            {mainRolesSaved ? "Close" : "Save"}
          </LoadingButton>
        </Box>
      </Stack>     
      <EditRole role={editRole} close={() => setEditRole(null)} userId={userId} />      
    </DialogContent>
  </Dialog>;
}
